import React, { useState, useEffect, ReactNode } from 'react';
import { Move, MoreHorizontal, X } from 'lucide-react';
import { WidgetInterface } from '../types';
import { StageButtonRow } from './StageButton.tsx';

interface WidgetProps {
  widget: WidgetInterface;
  currentStage?: number
  noStages : number
  textColor : string;
  snapValue : number;
  jitterX : number;
  jitterY : number;
  editable: boolean;
  children: ReactNode;
  onUpdate: (id: string, updates: Partial<WidgetInterface>) => void;
  onDelete: (id: string) => void;
  onRaise: (id: string) => void;
}

function toggleStageVisibility(stageList : boolean[], index : number) {
  if (!stageList) {
    stageList = [];
  }
  if (stageList.length <= index) {
    stageList = [...stageList, ...new Array(index-stageList.length+1).fill(true)];
  }

  stageList[index] = !stageList[index];
  return stageList;
}

export function Widget({ widget, currentStage, noStages, textColor, snapValue, jitterX, jitterY, editable, children, onUpdate, onDelete, onRaise }: WidgetProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [dragStart, setDragStart] = useState<{ x: number; y: number; widgetX: number; widgetY: number } | null>(null);
  const [resizeStart, setResizeStart] = useState<{ x: number; y: number; width: number; height: number } | null>(null);
  
  const snap = (value: number) : number => {
    return Math.round(value / snapValue) * snapValue;
  }

  const isCurrentStage = () : boolean => {
    if (!currentStage || !widget.stageVisibility)
      return true;

    return widget.stageVisibility[currentStage-1] ?? true;
    
  }

  const handleMouseDown = (e: React.MouseEvent, type: 'drag' | 'resize') => {
    if (!editable)
      return true;

    e.preventDefault();
    e.stopPropagation();
    
    if (type === 'drag') {
      setIsDragging(true);
      setDragStart({
        x: e.clientX,
        y: e.clientY,
        widgetX: widget.position.x,
        widgetY: widget.position.y
      });
    } else {
      setIsResizing(true);
      setResizeStart({
        x: e.clientX,
        y: e.clientY,
        width: widget.size.width,
        height: widget.size.height
      });
    }
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!editable)
        return;

      if (isDragging && dragStart) {
        const deltaX = e.clientX - dragStart.x;
        const deltaY = e.clientY - dragStart.y;
        
        onUpdate(widget.id, {
          position: {
            x: Math.min(Math.max(0, snap(dragStart.widgetX + deltaX)), window.innerWidth-50),
            y: Math.min(Math.max(-45, snap(dragStart.widgetY + deltaY)), window.innerHeight-50)
          }
        });
      }
      
      if (isResizing && resizeStart) {
        const deltaX = e.clientX - resizeStart.x;
        const deltaY = e.clientY - resizeStart.y;
        
        onUpdate(widget.id, {
          size: {
            width: Math.max(200, snap(resizeStart.width + deltaX)),
            height: Math.max(150, snap(resizeStart.height + deltaY))
          }
        });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setIsResizing(false);
      setDragStart(null);
      setResizeStart(null);
    };

    const removeCtxMenu = () => {setShowMenu(false)};

    if (isDragging || isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

      document.addEventListener('mouseup', removeCtxMenu);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('mouseup', removeCtxMenu);
    };
  }, [isDragging, isResizing, dragStart, resizeStart, widget.id, onUpdate]);

  return (
    <div
      className={`widget transition-fade ${isCurrentStage() ? "": "hidden-widget"} ${isDragging ? 'dragging' : ''} ${isResizing ? 'resizing' : ''} ${editable ? 'in-edit-mode semi-transparent-bg' : ''}` }
      style={{
        left: widget.position.x + Math.round(jitterX) * (editable ? 0 : 1),
        top: widget.position.y + Math.round(jitterY) * (editable ? 0 : 1),
        width: widget.size.width,
        height: widget.size.height,
        cursor: isDragging ? 'grabbing' : 'default',
        border: editable ? "1px solid #ffffff33" : "1px solid transparent",
        boxShadow: editable ? "0 8px 32px rgba(0, 0, 0, 0.1)" : "",
        display: editable && !isCurrentStage() ? "none" : "block"
      }}
      onClick={() => {
        onRaise(widget.id);
        return true;
      }}
    >
      {/* Header */}
      <div className="widget-header" onMouseDown={(e) => handleMouseDown(e, 'drag')} style={{cursor: editable ? (isDragging ? "grabbing": "grab") : "default", borderBottom: editable ? "inherit" : "none"}}>
        {editable && <Move size={16} />}
        <span className="widget-title" style={{display: editable ? "inline" : "none"}}>{widget.title}</span>
        
        <div style={{display: editable ? "block" : "none"}} className="relative">
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="widget-menu-button">
            <MoreHorizontal size={16} />
          </button>
          
          {showMenu && (
            <div className="widget-menu">
              <button
                onClick={() => {
                  onDelete(widget.id);
                  setShowMenu(false);
                }}
                className="widget-menu-item"
              >
                <X size={12} />
                Remove
              </button>
                <StageButtonRow active={widget.stageVisibility} onActivate={(index) => { onUpdate(widget.id, {stageVisibility: toggleStageVisibility(widget.stageVisibility, index)}) }} count={noStages} />
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="widget-content">
        {children}
      </div>

      {/* Resize handle */}
      <div
        onMouseDown={(e) => handleMouseDown(e, 'resize')}
        className="widget-resize-handle"
        style={{display: editable ? "block" : "none"}}
      >
        <div className="resize-indicator" style={{borderBottom : "6px solid "+textColor}} />
      </div>
    </div>
  );
}